import pricing from '@placekit/pricing';

import { translate } from 'i18n';
import { formatCompact, formatCount, formatPerRequest, formatUSD } from 'utils/format';

// Worked example on the pricing page: an online store filling 5,000 checkout
// addresses, ~10 keystrokes each. Every figure below is derived from
// `@placekit/pricing`: no price or quota is ever typed by hand.
const EXAMPLE_REQUESTS = 120000;
const STORE_REQUESTS = 50000;
const KEYSTROKES_PER_ADDRESS = 10;
const STORE_CHECKOUTS = STORE_REQUESTS / KEYSTROKES_PER_ADDRESS;

/**
 * Free monthly request quota, formatted ("10,000", "10 000").
 * @arg {string} [locale='en'] Locale code.
 * @return {string}
 */
export const formatQuota = (locale = 'en') => formatCount(pricing.quota, locale);

/**
 * Every number quoted about pricing, formatted for a locale.
 * @arg {string} [locale='en'] Locale code.
 * @return {object} Interpolation variables for the pricing dictionary keys.
 */
export const pricingVars = (locale = 'en') => {
  const price = (i) => formatPerRequest(pricing.tiers[i].price, locale);
  const storeAmount = pricing.compute(STORE_REQUESTS);
  return {
    quota: formatQuota(locale),
    quotaShort: formatCompact(pricing.quota, locale),
    checkouts: formatCount(STORE_CHECKOUTS, locale),
    requests: formatCount(STORE_REQUESTS, locale),
    billable: formatCount(STORE_REQUESTS - pricing.quota, locale),
    price: price(0),
    amount: formatUSD(storeAmount, locale),
    unit: formatUSD(storeAmount / STORE_CHECKOUTS, locale, '$0.000'),
    tier1: formatCount(pricing.tiers[1].from, locale),
    tier1PlusOne: formatCount(pricing.tiers[1].from + 1, locale),
    tier2: formatCount(pricing.tiers[2].from, locale),
    price0: price(0),
    price1: price(1),
    price2: price(2),
    priceLast: price(pricing.tiers.length - 1),
    example: formatCount(EXAMPLE_REQUESTS, locale),
    exampleAmount: formatUSD(pricing.compute(EXAMPLE_REQUESTS), locale),
    step0: formatCount(pricing.tiers[1].from - pricing.quota, locale),
    step1: formatCount(pricing.tiers[2].from - pricing.tiers[1].from, locale),
    step2: formatCount(EXAMPLE_REQUESTS - pricing.tiers[2].from, locale),
  };
};

const FAQ_KEYS = ['free', 'request', 'volume', 'billing', 'card', 'cap', 'discount', 'rateLimit'];

/**
 * FAQ questions and answers, resolved outside React so `getStaticProps` can feed
 * the exact same strings to the FAQPage JSON-LD that the page renders.
 * @arg {string} [locale='en'] Locale code.
 * @return {Array<{question: string, answer: string}>}
 */
export const faqItems = (locale = 'en') => {
  const vars = pricingVars(locale);
  return FAQ_KEYS.map((key) => translate(locale, `pricing.faq.items.${key}`, vars));
};
